import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Webinar } from '@proref/types';
import { useReserveWebinar, useWebinarAttendeesQuery } from '../../hooks/use-webinars';
import { downloadWebinarAttendeesCsv } from '../../services/webinars.service';
import { platformLabel, speakersOf } from '../../lib/webinar-platform';
import { Modal } from '../shared/Modal';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { Skeleton } from '../shared/Skeleton';
import { Icon } from '../shared/Icons';
import { notifyError, notifySuccess } from '../../lib/toast';

interface WebinarDetailModalProps {
  webinar: Webinar | null;
  open: boolean;
  onClose: () => void;
  // Whether a Reserve/Cancel action belongs in the footer at all. Defaults true so Home's usage
  // (referees only) is unchanged; the admin Webinars page passes the viewer's real capability,
  // since a super_admin (who runs the platform, not attends it) and a past webinar (already
  // happened) both have nothing to reserve.
  canReserve?: boolean;
  // Whether to show the host's attendee roster. Off by default (Home never hosts); the admin
  // Webinars page passes true for whoever may manage this webinar.
  canManage?: boolean;
}

// Weekday + time + optional duration, e.g. "Mon, Jul 27 · 19:00 · 60 min". Local wall-clock — the
// referee plans around when the webinar starts on their own device.
function whenLabel(iso: string, durationMin: number | null): string {
  const when = new Date(iso).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  return durationMin ? `${when} · ${durationMin} min` : when;
}

// "18 joining" plus "· N seats left" when the webinar is capped (unlimited webinars omit the tail).
// A plain function, not a component — takes `t` as a parameter, matching every other module-level
// helper in this codebase that needs translated text.
function attendanceLabel(t: TFunction, w: Webinar): string {
  const base = t('home.webinars.joining', { count: w.reservation_count });
  return w.seats_left === null ? base : `${base} · ${t('webinars.detail.seatsLeft', { count: w.seats_left })}`;
}

// Where the webinar happens, and — for whoever is entitled to it — the link itself.
//
// Three distinct states, because they mean very different things to a referee deciding whether to
// sign up: the link is here (join it), a link EXISTS but you haven't reserved (reserve to get it),
// or no link has been posted yet (nothing to do but wait). `join_url` is null in both of the last
// two, which is exactly why the API also sends `has_join_url`.
function JoinRow({ webinar }: { webinar: Webinar }) {
  const { t } = useTranslation();
  const label = platformLabel(webinar.platform, webinar.join_url);
  if (!label && !webinar.has_join_url) return null;

  async function onCopy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      notifySuccess(t('webinars.detail.linkCopiedToast'));
    } catch (err) {
      notifyError(err, t('webinars.detail.copyErrorToast'));
    }
  }

  return (
    <div className="flex items-center gap-2 rounded-sm bg-bg-2 px-3 py-2">
      <Icon.link className="h-4 w-4 flex-none text-muted" />
      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
        {label ?? t('webinars.detail.meetingLink')}
        {!webinar.join_url && (
          <span className="text-muted">
            {' · '}
            {webinar.has_join_url ? t('webinars.detail.reserveToGetLink') : t('webinars.detail.linkAnnounced')}
          </span>
        )}
      </span>
      {webinar.join_url && (
        <>
          <button
            type="button"
            aria-label={t('webinars.detail.copyLinkAria')}
            onClick={() => void onCopy(webinar.join_url as string)}
            className="grid h-7 w-7 flex-none place-items-center rounded-btn text-muted transition-colors hover:text-ink"
          >
            <Icon.copy className="h-3.5 w-3.5" />
          </button>
          {/* noreferrer alongside noopener: the meeting URL can carry a room id, and there is no
              reason to hand it to the target as a Referer header. */}
          <a
            href={webinar.join_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-none rounded-btn bg-accent px-2.5 py-1 text-[12px] font-medium text-accent-ink hover:bg-accent-2"
          >
            {t('webinars.detail.join')}
          </a>
        </>
      )}
    </div>
  );
}

// The webinar detail view — shared by Home's "Upcoming webinars" card and the admin Webinars page,
// so the two present the same facts about a webinar rather than two hand-drifted copies. Its footer
// adapts to who's looking: a referee gets the reserve/cancel confirm this started as; a host also
// gets the attendee roster, since "who signed up" is a host-only question the same modal can answer
// without a second dialog.
export function WebinarDetailModal({ webinar, open, onClose, canReserve = true, canManage = false }: WebinarDetailModalProps) {
  const { t } = useTranslation();
  const reserve = useReserveWebinar();
  // Only asked for while the modal is actually open on a manage-capable viewer — the API 403s
  // anyone else, so there's no reason to fire the request for a referee's own detail view.
  const attendeesQuery = useWebinarAttendeesQuery(webinar?.id, open && canManage);
  const [exporting, setExporting] = useState(false);
  // The webinar may briefly be null between closing and unmount; guard the render.
  if (!webinar) return null;

  const isFull = webinar.seats_left === 0 && !webinar.is_reserved;

  async function onConfirm() {
    if (!webinar) return;
    try {
      await reserve.mutateAsync(webinar.id);
      notifySuccess(t('webinars.detail.seatReservedToast'));
      onClose();
    } catch (err) {
      notifyError(err, t('webinars.detail.reserveErrorToast'));
    }
  }

  async function onExport() {
    if (!webinar) return;
    setExporting(true);
    try {
      await downloadWebinarAttendeesCsv(webinar.id, `${webinar.title}-attendees.csv`);
      notifySuccess(t('webinars.detail.exportedToast'));
    } catch (err) {
      notifyError(err, t('webinars.detail.exportErrorToast'));
    } finally {
      setExporting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={reserve.isPending ? () => {} : onClose}
      title={webinar.title}
      sub={canReserve ? t('webinars.detail.reserveYourSeat') : t('webinars.detail.webinarDetails')}
      footer={
        canReserve ? (
          <>
            <Button onClick={onClose} disabled={reserve.isPending}>
              {t('webinars.cancel')}
            </Button>
            {webinar.is_reserved ? (
              // Already reserved — nothing to confirm; make that plain and let them close.
              <Button variant="accent" disabled>
                <Icon.check className="h-3.5 w-3.5" /> {t('webinars.detail.reserved')}
              </Button>
            ) : (
              <Button variant="accent" onClick={onConfirm} disabled={reserve.isPending || isFull}>
                {reserve.isPending ? (
                  <>
                    <Spinner className="h-3.5 w-3.5" /> {t('webinars.detail.reserving')}
                  </>
                ) : isFull ? (
                  t('webinars.detail.webinarFull')
                ) : (
                  t('webinars.detail.confirmReservation')
                )}
              </Button>
            )}
          </>
        ) : (
          <Button onClick={onClose}>{t('webinars.detail.close')}</Button>
        )
      }
    >
      <div className="flex flex-col gap-3">
        {/* Scope + type pills. */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={
              'rounded-sm px-2 py-0.5 text-[11px] font-semibold ' +
              (webinar.type === 'live' ? 'bg-accent-tint text-accent' : 'bg-line-2 text-muted')
            }
          >
            {webinar.type === 'live' ? t('webinars.detail.live') : t('webinars.detail.group')}
          </span>
          {webinar.is_general && (
            <span className="rounded-sm bg-info/10 px-2 py-0.5 text-[11px] font-semibold text-info">
              {t('webinars.detail.generalAllSports')}
            </span>
          )}
        </div>

        {/* Meta rows — when, host, attendance. */}
        <dl className="flex flex-col gap-2 text-[13px] text-ink">
          <div className="flex items-center gap-2">
            <Icon.calendar className="h-4 w-4 flex-none text-muted" />
            <span>{whenLabel(webinar.scheduled_at, webinar.duration_min)}</span>
          </div>
          {webinar.host_name && (
            <div className="flex items-center gap-2">
              <Icon.users className="h-4 w-4 flex-none text-muted" />
              <span>{t('webinars.detail.hostedByCapital', { name: webinar.host_name })}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Icon.users className="h-4 w-4 flex-none text-muted" />
            <span>{attendanceLabel(t, webinar)}</span>
          </div>
        </dl>

        <JoinRow webinar={webinar} />

        {webinar.description && (
          <p className="text-[13px] leading-relaxed text-muted">{webinar.description}</p>
        )}

        {speakersOf(webinar).length > 0 && (
          <div className="flex flex-col gap-1.5 border-t border-line pt-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted">
              {t('webinars.detail.speaker', { count: speakersOf(webinar).length })}
            </h3>
            <ul className="flex flex-col gap-1.5">
              {speakersOf(webinar).map((s, i) => (
                // Speakers are free text with no id of their own; the list is short, static while
                // the modal is open, and never reordered, so the index is a safe key here.
                <li key={`${s.name}-${i}`} className="text-[13px] leading-snug">
                  <span className="font-medium text-ink">{s.name}</span>
                  {s.title && <span className="block text-[12px] text-muted">{s.title}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {canManage && (
          <div className="border-t border-line pt-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                {t('webinars.detail.attendees')}
                {attendeesQuery.data ? t('webinars.detail.attendeesCountSuffix', { count: attendeesQuery.data.length }) : ''}
              </h3>
              {/* Excel-openable CSV, not JSON — an admin wants this in a spreadsheet, not a screen
                  read. Disabled with nothing to export rather than downloading a one-line file. */}
              <button
                type="button"
                onClick={onExport}
                disabled={exporting || !attendeesQuery.data || attendeesQuery.data.length === 0}
                className="inline-flex flex-none items-center gap-1 text-[11px] font-medium text-accent hover:text-accent-2 disabled:cursor-not-allowed disabled:text-muted disabled:no-underline"
              >
                {exporting ? <Spinner className="h-3 w-3" /> : <Icon.doc className="h-3 w-3" />} {t('webinars.detail.exportCsv')}
              </button>
            </div>
            {attendeesQuery.isLoading ? (
              <div className="mt-2 space-y-1.5">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : attendeesQuery.isError ? (
              <p className="mt-2 text-[12px] text-danger">{t('webinars.detail.couldNotLoadAttendees')}</p>
            ) : attendeesQuery.data && attendeesQuery.data.length > 0 ? (
              <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                {attendeesQuery.data.map((a) => (
                  <li
                    key={a.user_id}
                    className="flex items-center justify-between gap-2 rounded-sm bg-bg-2 px-2.5 py-1.5 text-[12.5px]"
                  >
                    <span className="min-w-0 truncate text-ink">
                      {a.full_name}
                      {/* The webinar's own name already tells a federation-scoped roster who these
                          are; a GENERAL webinar draws from every federation, so that's where this
                          earns its place on screen (it's in the CSV export either way). */}
                      {webinar.is_general && a.federation_name && (
                        <span className="text-muted"> · {a.federation_name}</span>
                      )}
                    </span>
                    {a.licence_level && <span className="flex-none text-[11px] text-muted">{a.licence_level}</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-[12px] text-muted">{t('webinars.detail.nobodyReserved')}</p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
